import app from 'flarum/forum/app';

/**
 * Client-side audio: the background music loop and the short selection tick,
 * gated by the participant's own mute preference.
 *
 * Browsers refuse to start audio before the user has interacted with the
 * page, so background music is only (re)started from a click handler
 * (joining the lobby, picking an option) — never from a poll response.
 */

const STORAGE_KEY = 'happy-quiz-muted';

const EFFECT_FILES = {
  tick: 'tick.mp3',
};

let musicEl = null;
let musicTrack = null;
const effectPool = {};

function assetUrl(file) {
  return `${app.forum.attribute('assetsBaseUrl')}/extensions/happydigital-quiz/${file}`;
}

/**
 * Reads the participant's own preference. Defaults to unmuted; a corrupt or
 * missing value is treated the same as "never set" rather than as an error.
 */
export function isMuted() {
  try {
    return localStorage.getItem(STORAGE_KEY) === '1';
  } catch (e) {
    return false;
  }
}

export function setMuted(muted) {
  try {
    localStorage.setItem(STORAGE_KEY, muted ? '1' : '0');
  } catch (e) {
    // A blocked store (private mode, cleared site data) just means the
    // choice will not survive a reload — not worth surfacing.
  }

  if (muted && musicEl) {
    musicEl.pause();
  } else if (!muted && musicEl && musicTrack) {
    // Resuming is still subject to the browser's autoplay rules; a silent
    // failure here is expected and left alone.
    musicEl.play().catch(() => {});
  }
}

/**
 * Starts (or keeps playing) the given track, looped. Switching tracks or
 * stopping (`track` null/'none') tears down the previous element rather than
 * leaving it playing in the background.
 */
export function playMusic(track) {
  if (!track || track === 'none') {
    stopMusic();
    return;
  }

  if (musicTrack === track && musicEl) {
    if (isMuted()) return;
    musicEl.play().catch(() => {});
    return;
  }

  stopMusic();

  musicTrack = track;
  musicEl = new Audio(assetUrl(`${track}.mp3`));
  musicEl.loop = true;
  musicEl.volume = 0.35;

  if (!isMuted()) {
    musicEl.play().catch(() => {});
  }
}

export function stopMusic() {
  if (musicEl) {
    musicEl.pause();
    musicEl.src = '';
  }

  musicEl = null;
  musicTrack = null;
}

/**
 * Plays a short effect. A fresh `Audio` instance each time, so rapid repeats
 * (picking an option right after another) overlap cleanly instead of cutting
 * each other off.
 */
export function playEffect(name) {
  if (isMuted()) return;

  const file = EFFECT_FILES[name];

  if (!file) return;

  if (!effectPool[name]) {
    effectPool[name] = assetUrl(file);
  }

  const el = new Audio(effectPool[name]);
  el.volume = 0.6;
  el.play().catch(() => {});
}
